import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { enrollmentDataContext } from "../layout";
import {
  Clock,
  GraduationCap,
  BookOpen,
  Award,
  BarChart3,
  Calendar,
  PlayCircle,
  TrendingUp,
  CheckCircle2,
  ChevronRight,
  Share2,
  MapPin,
} from "lucide-react";
import axios from "axios";
import Button from "../../../components/common/Button";
import { useAuthState, useLogout } from "react-redux-django-auth";
import { authClientWeb } from "react-redux-django-auth/web";

const CircularProgress = ({ percentage, color = "#3e57da", size = 60 }) => {
  const radius = (size - 10) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div
      className="cht-relative cht-flex cht-items-center cht-justify-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="cht-transform -cht-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(0,0,0,0.05)"
          strokeWidth="4"
          fill="transparent"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth="4"
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="cht-transition-all cht-duration-1000 cht-ease-out"
        />
      </svg>
      <span className="cht-absolute cht-text-[10px] cht-font-bold cht-text-gray-700">
        {percentage}%
      </span>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, colorClass, isLoading }) => (
  <div className="cht-bg-white/70 cht-backdrop-blur-md cht-p-5 cht-rounded-[24px] cht-shadow-[0_10px_30px_rgba(0,0,0,0.03)] cht-border cht-border-slate-200/80 cht-flex cht-items-center cht-gap-4 hover:cht-scale-[1.02] cht-transition-all cht-duration-300">
    <div
      className={`cht-p-3 cht-rounded-[16px] ${colorClass.replace("bg-", "cht-bg-")} cht-bg-opacity-10`}
    >
      <Icon
        className={`cht-w-6 cht-h-6 ${colorClass.replace("bg-", "cht-text-")}`}
      />
    </div>
    <div className="cht-flex-1">
      <p className="cht-text-xs cht-font-medium cht-text-gray-400 cht-uppercase cht-tracking-wider">
        {title}
      </p>
      {isLoading ? (
        <div className="cht-h-8 cht-w-16 cht-bg-gray-200/80 cht-animate-pulse cht-rounded-md cht-mt-1"></div>
      ) : (
        <h3 className="cht-text-2xl cht-font-bold cht-text-[#1e293b] cht-mt-1">
          {value}
        </h3>
      )}
    </div>
  </div>
);

const getClient = () => {
  try {
    const client = authClientWeb();
    if (client) return client;
  } catch {
    // fallback if authClientWeb is not initialized yet
  }
  return axios.create();
};

const Overview = ({ onSwitchTab }: { onSwitchTab?: (tab: string) => void }) => {
  const { user: currentUser, isAuthenticated, isAuthLoading } = useAuthState();

  const navigate = useNavigate();
  const { inProgressCourses, completedCourses, inProgressTracks } = useContext(
    enrollmentDataContext,
  );

  const firstName = currentUser?.first_name || "Student";
  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  // Get dynamic greeting based on user's local time of day
  const getGreeting = () => {
    const hours = new Date().getHours();
    if (hours < 12) return "Good morning";
    if (hours < 17) return "Good afternoon";
    return "Good evening";
  };
  const greeting = getGreeting();

  // Merge courses and tracks
  const ongoing = [...(inProgressTracks || []), ...(inProgressCourses || [])];

  // Sort ongoing items by last active date descending
  const sortedOngoing = [...ongoing].sort((a, b) => {
    if (!a.last_active_date) return 1;
    if (!b.last_active_date) return -1;
    return (
      new Date(b.last_active_date).getTime() -
      new Date(a.last_active_date).getTime()
    );
  });

  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isCalendarMenuOpen, setIsCalendarMenuOpen] = useState(false);

  // Real-time academic performance analytics
  const [analytics, setAnalytics] = useState<any>({
    completed_lessons_count: 0,
    remaining_hours: 0,
    streak_days: 0,
    overall_progress_percent: 0,
    certificates_count: 0,
    up_next: null,
    assignments: [],
    assignments_summary: {
      total_assigned: 0,
      pending_count: 0,
      overdue_count: 0,
      graded_count: 0,
      average_score: 0,
      average_grade: "—",
    },
  });
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(true);

  const fetchAnalytics = async () => {
    try {
      setIsAnalyticsLoading(true);
      const client = getClient();
      const response = await client.get("/course/bk-learning-analytics/");
      if (response.data) {
        setAnalytics((prev: any) => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Error loading learning analytics:", error);
    } finally {
      setIsAnalyticsLoading(false);
    }
  };

  const formatToUTCBasic = (dateInput) => {
    try {
      const date = new Date(dateInput);
      return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    } catch (e) {
      return "";
    }
  };

  const handleDownloadSingleEventICS = (eventObj) => {
    const icsLines = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Codehub Technologies//CoreDesign//EN",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "BEGIN:VEVENT",
      `UID:${eventObj.id}@codehubtech.com`,
      `DTSTAMP:${formatToUTCBasic(new Date())}`,
      `DTSTART:${formatToUTCBasic(eventObj.start)}`,
      `DTEND:${formatToUTCBasic(eventObj.end)}`,
      `SUMMARY:${eventObj.title}`,
      `DESCRIPTION:${eventObj.description.replace(/\n/g, "\\n")}`,
      `LOCATION:${eventObj.location}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ];
    const icsContent = icsLines.join("\r\n");

    const blob = new Blob([icsContent], {
      type: "text/calendar;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const sanitizedTitle = eventObj.title
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "_");
    link.setAttribute("download", `${sanitizedTitle}.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Fetch events dynamically from the Django EventManagement backend API
  const loadEvents = async () => {
    try {
      setIsLoading(true);
      const client = getClient();
      const response = await client.get("/events/");
      if (response.data) {
        // Map backend REST fields to frontend timeline formats
        const mappedEvents = response.data.map((event) => {
          const startDateObj = new Date(event.start_date);
          const endDateObj = new Date(event.end_date);

          const timeStr =
            event.start_date && event.end_date
              ? `${startDateObj.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })} - ${endDateObj.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })}`
              : "All Day";

          const today = new Date();
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);

          let dateLabel = startDateObj.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
          if (startDateObj.toDateString() === today.toDateString()) {
            dateLabel = "Today";
          } else if (startDateObj.toDateString() === tomorrow.toDateString()) {
            dateLabel = "Tomorrow";
          }

          let statusLabel = "Upcoming";
          const diffMs = startDateObj.getTime() - today.getTime();
          if (startDateObj < today && endDateObj > today) {
            statusLabel = "Live Now";
          } else if (diffMs > 0 && diffMs < 3600000) {
            statusLabel = `Starts in ${Math.round(diffMs / 60000)}m`;
          } else if (event.event_type === "deadline") {
            statusLabel = "Due Soon";
          }

          return {
            id: event.id,
            title: event.title,
            description: event.description || "No description provided.",
            start: event.start_date,
            end: event.end_date,
            date: dateLabel,
            time: timeStr,
            location: event.location,
            color: event.color || "#3e57da",
            colorClass: event.color_class || "cht-bg-[#3e57da]",
            status: event.is_registered ? "RSVP confirmed" : statusLabel,
            type: event.event_type,
            meetingLink: event.meeting_link || "",
            host: event.instructor_detail?.name || "Academics Board",
            duration: event.meeting_link ? "1.5 hours" : "End of Day",
            isRegistered: event.is_registered || false,
          };
        });
        setEvents(mappedEvents);
      }
    } catch (error) {
      console.error("Error loading events:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthLoading) return;
    if (isAuthenticated) {
      loadEvents();
      fetchAnalytics();
    } else {
      setIsLoading(false);
      setIsAnalyticsLoading(false);
    }
  }, [isAuthLoading, isAuthenticated]);

  // API driven RSVP toggle action
  const handleRsvpToggle = async (eventObj) => {
    try {
      if (!isAuthenticated) {
        alert("Please log in to RSVP for events.");
        return;
      }
      const client = getClient();
      const response = await client.post(`/events/${eventObj.id}/rsvp/`);
      if (response.status === 200 || response.status === 201) {
        const nextReg = response.data.is_registered;

        // Update local list state
        setEvents((prev) =>
          prev.map((e) => {
            if (e.id === eventObj.id) {
              return {
                ...e,
                isRegistered: nextReg,
                status: nextReg ? "RSVP confirmed" : "Upcoming",
              };
            }
            return e;
          }),
        );

        // Update open details modal state if matching
        if (selectedEvent && selectedEvent.id === eventObj.id) {
          setSelectedEvent((prev) => ({
            ...prev,
            isRegistered: nextReg,
            status: nextReg ? "RSVP confirmed" : "Upcoming",
          }));
        }
      }
    } catch (error) {
      console.error("Error toggling RSVP:", error);
      alert("Failed to update RSVP. Please make sure you are logged in.");
    }
  };

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-slide-in-from-bottom-4 cht-duration-700">
      {/* Welcome Header */}
      <header className="cht-flex cht-flex-col md:cht-flex-row md:cht-items-end cht-justify-between cht-gap-4">
        <div>
          <p className="cht-text-sm cht-font-semibold cht-text-[#3e57da] cht-uppercase cht-tracking-[0.2em] cht-mb-2">
            {today}
          </p>
          <h1 className="cht-text-4xl cht-font-bold cht-text-[#1e293b] cht-tracking-tight">
            {greeting}, <span className="cht-text-[#3e57da]">{firstName}</span>
          </h1>
          <p className="cht-text-gray-500 cht-mt-2 cht-italic cht-font-medium">
            "The beautiful thing about learning is that no one can take it away
            from you."
          </p>
        </div>
      </header>

      <div className="cht-grid cht-grid-cols-12 cht-gap-8">
        {/* Main Content Area (8/12) */}
        <div className="cht-col-span-12 lg:cht-col-span-8 cht-space-y-8">
          {/* Learning Progress Section */}
          <section>
            <div className="cht-flex cht-items-center cht-justify-between cht-mb-4 cht-px-2">
              <h2 className="cht-text-xl cht-font-bold cht-text-[#1e293b]">
                Learning Progress
              </h2>
              <Button
                variant="ghost"
                size="sm"
                rightIcon={<ChevronRight size={14} />}
                onClick={() => navigate("/student/dashboard/my-courses")}
              >
                View all courses
              </Button>
            </div>

            <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 cht-gap-4">
              {sortedOngoing.slice(0, 2).map((item) => {
                const isCourse = !!item.course_detail;
                const id = isCourse
                  ? item.course_detail?.id || item.course
                  : item.track_detail?.id || item.track;
                const slug = isCourse
                  ? item.course_detail?.slug
                  : item.track_detail?.slug;
                const title = isCourse
                  ? item.course_detail?.title || "Professional Course"
                  : item.track_detail?.title || "Career Track";
                const progressVal = item.progress_percent ?? item.progress ?? 0;
                const categoryLabel = isCourse
                  ? "Current Course"
                  : "Career Track";

                return (
                  <div
                    key={item.id}
                    onClick={() =>
                      navigate(
                        isCourse
                          ? `/student/dashboard/courses/${slug}/enrolled?courseId=${id}`
                          : `/student/dashboard/track/${slug}/enrolled?trackId=${id}`,
                      )
                    }
                    className="cht-bg-white cht-p-6 cht-rounded-[24px] cht-shadow-[0_10px_30px_rgba(0,0,0,0.02)] cht-border cht-border-slate-200/80 cht-flex cht-items-center cht-justify-between cht-group hover:cht-shadow-lg hover:cht-border-[#3e57da]/30 hover:-cht-translate-y-1 cht-cursor-pointer cht-transition-all cht-duration-300"
                  >
                    <div className="cht-space-y-2 cht-max-w-[70%]">
                      <p className="cht-text-xs cht-font-bold cht-text-[#3e57da] cht-uppercase cht-tracking-wider">
                        {categoryLabel}
                      </p>
                      <h4 className="cht-text-lg cht-font-bold cht-text-[#1e293b] cht-truncate group-hover:cht-text-[#3e57da] transition-colors">
                        {title}
                      </h4>
                      <div className="cht-flex cht-items-center cht-gap-2 cht-text-gray-400">
                        <Clock size={14} />
                        <span className="cht-text-xs cht-font-medium">
                          {progressVal === 100
                            ? "Completed"
                            : "Resume Learning"}
                        </span>
                      </div>
                    </div>
                    <CircularProgress percentage={progressVal} size={70} />
                  </div>
                );
              })}

              {sortedOngoing.length === 0 && (
                <div className="cht-col-span-full cht-bg-white/50 cht-border-2 cht-border-dashed cht-border-gray-200 cht-p-10 cht-rounded-[24px] cht-flex cht-flex-col cht-items-center cht-justify-center cht-text-center cht-space-y-4">
                  <div className="cht-w-16 cht-h-16 cht-bg-gray-100 cht-rounded-full cht-flex cht-items-center cht-justify-center">
                    <BookOpen className="cht-text-gray-400 cht-w-8 cht-h-8" />
                  </div>
                  <div>
                    <h4 className="cht-text-lg cht-font-bold cht-text-gray-700">
                      No active progress
                    </h4>
                    <p className="cht-text-sm cht-text-gray-500">
                      Explore our catalog and start your learning journey today.
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    isPill
                    size="md"
                    onClick={() => navigate("/student/dashboard/courses")}
                  >
                    Browse Courses
                  </Button>
                </div>
              )}
            </div>
          </section>

          {/* Quick Stats Grid */}
          <section>
            <h2 className="cht-text-xl cht-font-bold cht-text-[#1e293b] cht-mb-4 cht-px-2">
              Your Performance
            </h2>
            <div className="cht-grid cht-grid-cols-2 md:cht-grid-cols-4 cht-gap-4">
              <StatCard
                title="Lessons"
                value={analytics.completed_lessons_count}
                icon={CheckCircle2}
                colorClass="bg-green-500"
                isLoading={isAnalyticsLoading}
              />
              <StatCard
                title="Remaining"
                value={`${analytics.remaining_hours} hrs`}
                icon={Clock}
                colorClass="bg-purple-500"
                isLoading={isAnalyticsLoading}
              />
              <StatCard
                title="Streak"
                value={`${analytics.streak_days}d`}
                icon={TrendingUp}
                colorClass="bg-orange-500"
                isLoading={isAnalyticsLoading}
              />
              <StatCard
                title="Progress"
                value={`${analytics.overall_progress_percent}%`}
                icon={Award}
                colorClass="bg-blue-500"
                isLoading={isAnalyticsLoading}
              />
            </div>
          </section>

          {/* Community Announcement / Featured Event */}
          <section className="cht-bg-gradient-to-r cht-from-[#3e57da] cht-to-[#6a82fb] cht-p-6 sm:cht-p-8 cht-rounded-[24px] cht-text-white cht-relative cht-overflow-hidden cht-group cht-shadow-xl">
            <div className="cht-relative cht-z-10 cht-flex cht-flex-col md:cht-flex-row cht-items-center cht-justify-between cht-gap-6">
              {events.length > 0 ? (
                <>
                  <div className="cht-space-y-3">
                    <div className="cht-bg-white/20 cht-backdrop-blur-md cht-px-3 cht-py-1 cht-rounded-full cht-text-[10px] cht-font-bold cht-uppercase cht-tracking-widest cht-w-fit">
                      {events[0].type || "Upcoming Event"}
                    </div>
                    <h3 className="cht-text-2xl cht-font-bold">
                      {events[0].title}
                    </h3>
                    <p className="cht-text-white/80 cht-max-w-md cht-font-medium cht-line-clamp-2">
                      {events[0].description}
                    </p>
                    <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-4 cht-text-sm cht-font-semibold cht-pt-2">
                      <span className="cht-flex cht-items-center cht-gap-2">
                        <Calendar size={16} /> {events[0].date}, {events[0].time}
                      </span>
                      <span className="cht-flex cht-items-center cht-gap-2">
                        <PlayCircle size={16} /> {events[0].location}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    isPill
                    className="cht-bg-white/90 cht-text-[#3e57da] hover:cht-bg-white"
                    onClick={() => setSelectedEvent(events[0])}
                  >
                    {events[0].isRegistered ? "View Details" : "Set Reminder"}
                  </Button>
                </>
              ) : (
                <>
                  <div className="cht-space-y-3">
                    <div className="cht-bg-white/20 cht-backdrop-blur-md cht-px-3 cht-py-1 cht-rounded-full cht-text-[10px] cht-font-bold cht-uppercase cht-tracking-widest cht-w-fit">
                      Community Hub
                    </div>
                    <h3 className="cht-text-2xl cht-font-bold">
                      Connect with Peers & Mentors
                    </h3>
                    <p className="cht-text-white/80 cht-max-w-md cht-font-medium">
                      Collaborate on projects, ask questions, and participate in live technical discussions inside the Student Community.
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    isPill
                    className="cht-bg-white/90 cht-text-[#3e57da] hover:cht-bg-white"
                    onClick={() => navigate("/student/dashboard/community")}
                  >
                    Open Community
                  </Button>
                </>
              )}
            </div>
            {/* Decorative background circle */}
            <div className="cht-absolute -cht-right-20 -cht-bottom-20 cht-w-64 cht-h-64 cht-bg-white/10 cht-rounded-full cht-blur-3xl cht-transition-all cht-duration-700 group-hover:cht-scale-125"></div>
          </section>
        </div>

        {/* Sidebar Area (4/12) */}
        <div className="cht-col-span-12 lg:cht-col-span-4 cht-space-y-8">
          {/* Up Next Action Center */}
          <section className="cht-bg-[#1e293b] cht-text-white cht-p-6 sm:cht-p-8 cht-rounded-[28px] cht-shadow-2xl cht-relative cht-overflow-hidden cht-group">
            <div className="cht-relative cht-z-10 cht-space-y-6">
              <div className="cht-flex cht-items-center cht-justify-between">
                <h3 className="cht-text-lg cht-font-bold">Up Next</h3>
                <BarChart3 className="cht-text-gray-500" size={20} />
              </div>

              {analytics.up_next ? (
                <>
                  <div className="cht-space-y-1">
                    <p className="cht-text-xs cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-widest">
                      {analytics.up_next.track_title || analytics.up_next.course_title || "Immediate Priority"}
                    </p>
                    <h4 className="cht-text-xl cht-font-bold cht-leading-tight">
                      Module {analytics.up_next.module_number}: {analytics.up_next.module_title}
                    </h4>
                  </div>

                  <div className="cht-space-y-4">
                    <div className="cht-flex cht-items-center cht-gap-3 cht-bg-white/5 cht-p-3 cht-rounded-[16px] cht-border cht-border-white/5">
                      <div className="cht-w-10 cht-h-10 cht-bg-[#3e57da] cht-rounded-[12px] cht-flex cht-items-center cht-justify-center cht-shadow-inner cht-shrink-0">
                        <PlayCircle size={20} className="cht-text-white" />
                      </div>
                      <div className="cht-min-w-0">
                        <p className="cht-text-sm cht-font-bold cht-truncate">
                          {analytics.up_next.lesson_title}
                        </p>
                        <p className="cht-text-[10px] cht-text-gray-400">
                          {analytics.up_next.duration}
                        </p>
                      </div>
                    </div>

                    <Button
                      variant="secondary"
                      isPill
                      className="cht-w-full"
                      rightIcon={<TrendingUp size={16} />}
                      onClick={() =>
                        navigate(
                          analytics.up_next.is_track && analytics.up_next.track_slug
                            ? `/student/dashboard/track/${analytics.up_next.track_slug}/enrolled?trackId=${analytics.up_next.track_id}`
                            : `/student/dashboard/courses/${analytics.up_next.course_slug}/enrolled?courseId=${analytics.up_next.course_id}`,
                        )
                      }
                    >
                      Resume Learning
                    </Button>
                  </div>
                </>
              ) : sortedOngoing.length > 0 ? (
                <>
                  <div className="cht-space-y-1">
                    <p className="cht-text-xs cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-widest">
                      Active Program
                    </p>
                    <h4 className="cht-text-xl cht-font-bold cht-leading-tight">
                      {sortedOngoing[0].course_detail?.title ||
                        sortedOngoing[0].track_detail?.title ||
                        "Continue Your Curriculum"}
                    </h4>
                  </div>

                  <div className="cht-space-y-4">
                    <div className="cht-flex cht-items-center cht-gap-3 cht-bg-white/5 cht-p-3 cht-rounded-[16px] cht-border cht-border-white/5">
                      <div className="cht-w-10 cht-h-10 cht-bg-[#3e57da] cht-rounded-[12px] cht-flex cht-items-center cht-justify-center cht-shadow-inner cht-shrink-0">
                        <PlayCircle size={20} className="cht-text-white" />
                      </div>
                      <div>
                        <p className="cht-text-sm cht-font-bold">
                          {sortedOngoing[0].progress_percent ?? 0}% Completed
                        </p>
                        <p className="cht-text-[10px] cht-text-gray-400">
                          Pick up where you left off
                        </p>
                      </div>
                    </div>

                    <Button
                      variant="secondary"
                      isPill
                      className="cht-w-full"
                      rightIcon={<TrendingUp size={16} />}
                      onClick={() => {
                        const first = sortedOngoing[0];
                        const isCourse = !!first.course_detail;
                        const id = isCourse
                          ? first.course_detail?.id || first.course
                          : first.track_detail?.id || first.track;
                        const slug = isCourse
                          ? first.course_detail?.slug
                          : first.track_detail?.slug;
                        navigate(
                          isCourse
                            ? `/student/dashboard/courses/${slug}/enrolled?courseId=${id}`
                            : `/student/dashboard/track/${slug}/enrolled?trackId=${id}`,
                        );
                      }}
                    >
                      Resume Learning
                    </Button>
                  </div>
                </>
              ) : (
                <div className="cht-space-y-4">
                  <div className="cht-space-y-1">
                    <p className="cht-text-xs cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-widest">
                      Get Started
                    </p>
                    <h4 className="cht-text-xl cht-font-bold cht-leading-tight">
                      No active lessons queued
                    </h4>
                  </div>
                  <p className="cht-text-xs cht-text-gray-400 cht-leading-relaxed">
                    Enroll in a course or career track from the library to unlock your personalized learning queue.
                  </p>
                  <Button
                    variant="secondary"
                    isPill
                    className="cht-w-full"
                    rightIcon={<ChevronRight size={16} />}
                    onClick={() => navigate("/student/dashboard/courses")}
                  >
                    Explore Library
                  </Button>
                </div>
              )}
            </div>
            {/* Background glow */}
            <div className="cht-absolute -cht-top-10 -cht-right-10 cht-w-32 cht-h-32 cht-bg-[#3e57da] cht-rounded-full cht-blur-[80px] cht-opacity-40"></div>
          </section>

          {/* Upcoming Agenda */}
          <div className="cht-bg-white cht-p-8 cht-rounded-[32px] cht-border cht-border-gray-50 cht-shadow-sm cht-space-y-8">
            <div className="cht-flex cht-items-center cht-justify-between">
              <h4 className="cht-text-xs cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-[0.2em]">
                Upcoming Agenda
              </h4>
              <div className="cht-p-2 cht-bg-gray-50 cht-rounded-lg cht-text-[#3e57da]">
                <Calendar size={14} />
              </div>
            </div>

            <div className="cht-space-y-6">
              {events.length > 0 ? (
                events.slice(0, 3).map((item) => (
                  <div
                    key={item.id}
                    onClick={() => setSelectedEvent(item)}
                    className="cht-flex cht-gap-4 cht-group cht-cursor-pointer"
                  >
                    <div
                      className={`cht-w-1.5 cht-h-16 ${item.colorClass} cht-rounded-full cht-shrink-0 cht-transition-all group-hover:cht-scale-y-110`}
                    ></div>
                    <div className="cht-space-y-1.5 cht-flex-1">
                      <div className="cht-flex cht-items-center cht-justify-between">
                        <h6 className="cht-text-sm cht-font-black cht-text-black group-hover:cht-text-[#3e57da] transition-colors">
                          {item.title}
                        </h6>
                        <span
                          className={`cht-text-[9px] cht-font-bold cht-px-2 cht-py-0.5 cht-rounded-md ${item.status.includes("Starts") ? "cht-bg-blue-50 cht-text-blue-600" : "cht-bg-orange-50 cht-text-orange-600"}`}
                        >
                          {item.status}
                        </span>
                      </div>
                      <div className="cht-flex cht-items-center cht-gap-4 cht-text-[10px] cht-font-bold cht-text-gray-400 uppercase tracking-widest">
                        <span className="cht-flex cht-items-center cht-gap-1.5">
                          <Clock size={12} /> {item.time}
                        </span>
                        <span className="cht-flex cht-items-center cht-gap-1.5">
                          <MapPin size={12} /> {item.location}
                        </span>
                      </div>
                      <div className="cht-pt-1">
                        <span className="cht-text-[10px] cht-font-black cht-text-gray-300 cht-uppercase cht-tracking-widest">
                          {item.date}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="cht-py-10 cht-text-center">
                  <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    {isLoading ? "Loading agenda..." : "No events found"}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Upcoming Assignments / Deadlines */}
          <section className="cht-bg-white/40 cht-backdrop-blur-sm cht-p-6 cht-rounded-[24px] cht-border cht-border-white/60">
            <h3 className="cht-text-lg cht-font-bold cht-text-[#1e293b] cht-mb-6 cht-flex cht-items-center cht-justify-between">
              Assignments
              <span
                className={`cht-text-white cht-text-[10px] cht-font-bold cht-px-2.5 cht-py-1 cht-rounded-full ${
                  (analytics.assignments_summary?.pending_count || 0) > 0
                    ? "cht-bg-red-500"
                    : "cht-bg-emerald-500"
                }`}
              >
                {analytics.assignments_summary?.pending_count || 0} DUE
              </span>
            </h3>

            <div className="cht-space-y-4">
              {Array.isArray(analytics.assignments) && analytics.assignments.length > 0 ? (
                analytics.assignments.slice(0, 3).map((item: any) => (
                  <div
                    key={item.id}
                    onClick={() =>
                      navigate(
                        `/student/dashboard/courses/${item.course_slug}/enrolled?courseId=${item.course_id}`,
                      )
                    }
                    className="cht-flex cht-items-start cht-gap-4 cht-group cht-cursor-pointer"
                  >
                    <div
                      className={`cht-mt-1.5 cht-w-2 cht-h-2 cht-rounded-full cht-ring-4 ${
                        item.status === "Graded"
                          ? "cht-bg-emerald-500 cht-ring-emerald-500/10"
                          : item.status === "Overdue"
                          ? "cht-bg-red-500 cht-ring-red-500/10"
                          : "cht-bg-[#3e57da] cht-ring-[#3e57da]/10"
                      }`}
                    ></div>
                    <div className="cht-space-y-1 cht-flex-1 cht-min-w-0">
                      <p className="cht-text-sm cht-font-bold cht-text-[#1e293b] group-hover:cht-text-[#3e57da] cht-transition-colors cht-line-clamp-1">
                        {item.title}
                      </p>
                      <div className="cht-flex items-center cht-justify-between cht-text-[10px] cht-font-semibold cht-text-gray-400 cht-gap-2">
                        <span className="cht-truncate">{item.program_label}</span>
                        <span
                          className={`cht-shrink-0 ${
                            item.status === "Overdue"
                              ? "cht-text-red-500"
                              : item.status === "Graded"
                              ? "cht-text-emerald-600"
                              : "cht-text-amber-600"
                          }`}
                        >
                          {item.score != null && item.status === "Graded"
                            ? `${item.score}%`
                            : item.due_relative}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="cht-py-6 cht-text-center">
                  <p className="cht-text-xs cht-font-semibold cht-text-gray-400">
                    {isAnalyticsLoading
                      ? "Loading assignments..."
                      : "No pending assignments right now."}
                  </p>
                </div>
              )}
            </div>

            <Button
              variant="ghost"
              isPill
              className="cht-w-full cht-mt-8 cht-bg-gray-100/80 cht-text-gray-900"
              onClick={() => {
                if (onSwitchTab) {
                  onSwitchTab("assignments");
                }
              }}
            >
              View Action Center
            </Button>
          </section>
        </div>
      </div>

      {/* Dynamic Glassmorphism Details Modal */}
      {selectedEvent && (
        <div className="cht-fixed cht-inset-0 cht-z-[9999] cht-flex cht-items-center cht-justify-center cht-overflow-y-auto cht-p-4">
          <div
            onClick={() => setSelectedEvent(null)}
            className="cht-fixed cht-inset-0 cht-h-full cht-w-full cht-bg-black/40 cht-backdrop-blur-md transition-opacity"
          ></div>

          <div className="cht-relative cht-bg-white/95 cht-backdrop-blur-2xl cht-w-full cht-max-w-xl cht-rounded-[32px] cht-p-8 sm:cht-p-10 cht-shadow-2xl cht-border cht-border-white/40 cht-space-y-6 cht-animate-in cht-zoom-in-95 cht-duration-300">
            <div className="cht-flex cht-items-start cht-justify-between">
              <div className="cht-space-y-2">
                <span
                  className={`cht-text-[9px] cht-font-black cht-px-3 cht-py-1.5 cht-rounded-xl cht-uppercase cht-tracking-widest ${selectedEvent.colorClass} cht-text-white`}
                >
                  {selectedEvent.type}
                </span>
                <h3 className="cht-text-xl sm:cht-text-2xl cht-font-black cht-text-black cht-mt-2">
                  {selectedEvent.title}
                </h3>
              </div>
              <button
                onClick={() => setSelectedEvent(null)}
                className="cht-p-2 cht-bg-gray-100 hover:cht-bg-gray-200 cht-rounded-full cht-transition-colors"
              >
                <svg
                  className="cht-w-4 cht-h-4 cht-text-gray-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="cht-space-y-2">
              <h5 className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                About the Event
              </h5>
              <p className="cht-text-xs sm:cht-text-sm cht-text-gray-600 cht-font-medium cht-leading-relaxed">
                {selectedEvent.description}
              </p>
            </div>

            <div className="cht-grid cht-grid-cols-2 cht-gap-4 cht-bg-gray-50/50 cht-p-4 cht-rounded-[20px] cht-border cht-border-gray-100">
              <div className="cht-space-y-1">
                <span className="cht-text-[9px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                  Time
                </span>
                <div className="cht-flex cht-items-center cht-gap-2 cht-text-xs cht-font-bold cht-text-black">
                  <Clock size={14} className="cht-text-[#3e57da]" />{" "}
                  {selectedEvent.time}
                </div>
              </div>
              <div className="cht-space-y-1">
                <span className="cht-text-[9px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                  Location
                </span>
                <div className="cht-flex cht-items-center cht-gap-2 cht-text-xs cht-font-bold cht-text-black">
                  <MapPin size={14} className="cht-text-[#3e57da]" />{" "}
                  {selectedEvent.location}
                </div>
              </div>
              <div className="cht-space-y-1">
                <span className="cht-text-[9px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                  Host / Instructor
                </span>
                <div className="cht-text-xs cht-font-bold cht-text-black">
                  {selectedEvent.host}
                </div>
              </div>
              <div className="cht-space-y-1">
                <span className="cht-text-[9px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                  Duration
                </span>
                <div className="cht-text-xs cht-font-bold cht-text-black">
                  {selectedEvent.duration}
                </div>
              </div>
            </div>

            <div className="cht-flex cht-flex-col sm:cht-flex-row cht-gap-4 cht-border-t cht-border-gray-100 cht-pt-6">
              {selectedEvent.meetingLink && (
                <Button
                  as="a"
                  href={selectedEvent.meetingLink}
                  target={
                    selectedEvent.meetingLink.startsWith("http")
                      ? "_blank"
                      : "_self"
                  }
                  rel="noopener noreferrer"
                  variant="primary"
                  className="cht-flex-1"
                >
                  {selectedEvent.type === "deadline"
                    ? "Submit Project"
                    : "Join Session"}
                </Button>
              )}
              {selectedEvent.type !== "deadline" && (
                <Button
                  variant={selectedEvent.isRegistered ? "ghost" : "outline"}
                  className={`cht-flex-1 ${selectedEvent.isRegistered ? "cht-text-green-600 cht-bg-green-50 hover:cht-bg-green-100" : ""}`}
                  onClick={() => handleRsvpToggle(selectedEvent)}
                >
                  {selectedEvent.isRegistered
                    ? "✓ RSVP confirmed"
                    : "RSVP for Event"}
                </Button>
              )}

              <div className="cht-relative cht-flex-1">
                <Button
                  variant="outline"
                  className="cht-w-full"
                  onClick={() => setIsCalendarMenuOpen(!isCalendarMenuOpen)}
                >
                  Add to Calendar
                </Button>

                {isCalendarMenuOpen && (
                  <div className="cht-absolute cht-bottom-full cht-left-0 cht-mb-2 cht-w-full cht-bg-white/95 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-rounded-2xl cht-shadow-xl cht-py-2 cht-z-20 cht-animate-in cht-fade-in cht-slide-in-from-bottom-2 cht-duration-200">
                    <a
                      href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(selectedEvent.title)}&details=${encodeURIComponent(selectedEvent.description)}&location=${encodeURIComponent(selectedEvent.location)}&dates=${formatToUTCBasic(selectedEvent.start)}/${formatToUTCBasic(selectedEvent.end)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cht-block cht-px-4 cht-py-2.5 cht-text-xs cht-font-black cht-text-gray-700 hover:cht-bg-gray-50 hover:cht-text-[#3e57da] transition-colors"
                      onClick={() => setIsCalendarMenuOpen(false)}
                    >
                      Google Calendar
                    </a>
                    <a
                      href={`https://outlook.office.com/calendar/deeplink/compose?path=/calendar/action/compose&rru=addevent&subject=${encodeURIComponent(selectedEvent.title)}&body=${encodeURIComponent(selectedEvent.description)}&location=${encodeURIComponent(selectedEvent.location)}&startdt=${formatToUTCBasic(selectedEvent.start)}&enddt=${formatToUTCBasic(selectedEvent.end)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cht-block cht-px-4 cht-py-2.5 cht-text-xs cht-font-black cht-text-gray-700 hover:cht-bg-gray-50 hover:cht-text-[#3e57da] transition-colors"
                      onClick={() => setIsCalendarMenuOpen(false)}
                    >
                      Outlook Calendar
                    </a>
                    <a
                      href={`https://calendar.yahoo.com/?v=60&view=d&type=20&title=${encodeURIComponent(selectedEvent.title)}&desc=${encodeURIComponent(selectedEvent.description)}&in_loc=${encodeURIComponent(selectedEvent.location)}&st=${formatToUTCBasic(selectedEvent.start)}&et=${formatToUTCBasic(selectedEvent.end)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cht-block cht-px-4 cht-py-2.5 cht-text-xs cht-font-black cht-text-gray-700 hover:cht-bg-gray-50 hover:cht-text-[#3e57da] transition-colors"
                      onClick={() => setIsCalendarMenuOpen(false)}
                    >
                      Yahoo Calendar
                    </a>
                    <button
                      onClick={() => {
                        handleDownloadSingleEventICS(selectedEvent);
                        setIsCalendarMenuOpen(false);
                      }}
                      className="cht-w-full cht-text-left cht-block cht-px-4 cht-py-2.5 cht-text-xs cht-font-black cht-text-gray-700 hover:cht-bg-gray-50 hover:cht-text-[#3e57da] transition-colors"
                    >
                      Apple Calendar / iCal
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Overview;
